import * as React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha } from '@mui/material/styles';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AppConfig from '../services/app-config';
import AuthService from '../services/auth.js';
import GestionTypeIcon from '../components/GestionTypeIcon';
import PageHeader from '../components/PageHeader';
import { optimizeFile, optimizeImage, optimizeVideo, optimizePdf } from '../utils/fileOptimize';

const cardSx = {
  borderRadius: '10px',
  boxShadow: 0,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper',
};

const sectionTitleSx = {
  fontSize: 14,
  fontWeight: 800,
  color: 'text.primary',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

// Resolve the effective allowed file types: type config takes precedence,
// falling back to the org-level gestiones config from AppConfig.
function effectiveAllowedTypes(typeConfig) {
  if (typeConfig && Array.isArray(typeConfig.tipos_archivo_permitidos) && typeConfig.tipos_archivo_permitidos.length > 0) {
    return typeConfig.tipos_archivo_permitidos;
  }
  const raw = AppConfig.get('gestiones_allowed_file_types');
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return ['image/jpeg', 'image/png', 'image/webp'];
}

function effectiveMaxMb(typeConfig) {
  if (typeConfig && typeConfig.tamano_max_archivo_mb) {
    return typeConfig.tamano_max_archivo_mb;
  }
  const raw = AppConfig.get('gestiones_max_file_mb');
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

// Build the accept attribute string for a file/image field from the effective allowed list.
function buildAccept(typeConfig) {
  const allowed = effectiveAllowedTypes(typeConfig);
  return allowed.length > 0 ? allowed.join(',') : '*';
}

// Validate a file list against the effective MIME + size limits.
function validateFiles(files, typeConfig, fieldName) {
  const allowed = effectiveAllowedTypes(typeConfig);
  const maxMb = effectiveMaxMb(typeConfig);
  for (const f of files) {
    if (allowed.length > 0 && !allowed.includes(f.type)) {
      return { ok: false, msg: `Tipo de archivo no permitido para ${fieldName}: ${f.name} (${f.type || 'sin tipo'})` };
    }
    if (maxMb && f.size > maxMb * 1024 * 1024) {
      return { ok: false, msg: `El archivo ${f.name} excede el tamaño máximo de ${maxMb}MB` };
    }
  }
  return { ok: true };
}

function FieldRenderer({ field, value, onChange, error, typeConfig }) {
  const label = field.label + (field.required ? ' *' : '');
  switch (field.type) {
    case 'text':
      return (
        <TextField
          fullWidth
          size="medium"
          label={label}
          variant="outlined"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error || ''}
        />
      );
    case 'number':
      return (
        <TextField
          fullWidth
          size="medium"
          type="number"
          label={label}
          variant="outlined"
          value={value === undefined || value === null ? '' : value}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          error={!!error}
          helperText={error || ''}
        />
      );
    case 'select':
      return (
        <FormControl fullWidth size="medium" error={!!error}>
          <InputLabel id={`sel-${field.key}-label`}>{label}</InputLabel>
          <Select
            labelId={`sel-${field.key}-label`}
            id={`sel-${field.key}`}
            label={label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            {(field.options || []).map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
          {error && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{error}</Typography>}
        </FormControl>
      );
    case 'date':
      return (
        <TextField
          fullWidth
          size="medium"
          type="date"
          label={label}
          variant="outlined"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error || ''}
          InputLabelProps={{ shrink: true }}
        />
      );
    case 'boolean':
      return (
        <FormControlLabel
          control={<Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
          label={field.label}
        />
      );
    case 'file':
    case 'image': {
      // Files are stored at the page level (key -> File[]); value is an array.
      const files = Array.isArray(value) ? value : [];
      const isImage = field.type === 'image';
      const accept = isImage ? 'image/*' : buildAccept(typeConfig);
      const helperAllowed = effectiveAllowedTypes(typeConfig);
      const helperMaxMb = effectiveMaxMb(typeConfig);
      return (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>{label}</Typography>
          {files.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {files.map((f, idx) => (
                <Chip
                  key={`f-${idx}`}
                  icon={<AttachFileIcon sx={{ fontSize: '0.9rem' }} />}
                  label={f.name}
                  size="small"
                  onDelete={() => onChange(files.filter((_, i) => i !== idx), true)}
                  sx={{ height: 24, fontSize: '0.7rem' }}
                />
              ))}
            </Box>
          )}
          <Box
            sx={{
              border: '2px dashed', borderColor: 'divider', borderRadius: '12px', p: 2,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center',
            }}
          >
            <Avatar sx={{ width: 40, height: 40, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'secondary.main' }}>
              {isImage ? <AddPhotoAlternateIcon sx={{ fontSize: 22 }} /> : <AttachFileIcon sx={{ fontSize: 22 }} />}
            </Avatar>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
              Arrastra o pulsa para seleccionar
            </Typography>
            <label htmlFor={`file-${field.key}`}>
              <Button variant="contained" color="secondary" component="span" startIcon={isImage ? <PhotoCameraOutlinedIcon /> : <AttachFileIcon />} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}>
                Agregar{isImage ? ' imagen' : ' archivo'}
              </Button>
              <Input
                type="file"
                inputProps={{ multiple: true, accept }}
                onChange={(e) => {
                  const picked = Array.from(e.target.files || []);
                  onChange(picked, true, picked);
                }}
                id={`file-${field.key}`}
                sx={{ display: 'none' }}
              />
            </label>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
              Permitidos: {helperAllowed.join(', ') || '—'} · Máx: {helperMaxMb}MB
            </Typography>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
          </Box>
        </Box>
      );
    }
    default:
      return (
        <TextField
          fullWidth
          size="medium"
          label={label}
          variant="outlined"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error || ''}
        />
      );
  }
}

export default function NewGestion({ setNavbarState, navbarState }) {
  const { typeKey } = useParams();
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [typeConfig, setTypeConfig] = React.useState(null);
  const [titulo, setTitulo] = React.useState('');
  const [descripcion, setDescripcion] = React.useState('');
  const [campos, setCampos] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [generalAttachments, setGeneralAttachments] = React.useState([]);
  const [optimizing, setOptimizing] = React.useState(false);
  const [optimizeProgress, setOptimizeProgress] = React.useState(0);

  const isAdmin = AuthService.userRoleIncludes(['admin']);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchTypeConfig = React.useCallback(async () => {
    try {
      const resp = await apiClient.get('gestiones/types', {}, { action: 'fetch_gestion_types' });
      if (resp.status === 200) {
        const types = (resp.data && resp.data.data && resp.data.data.types) || [];
        const cfg = types.find((t) => t.key === typeKey) || null;
        setTypeConfig(cfg);
        // Pre-seed campos state for the rendered fields (all fields for
        // admins; only residente_editable fields for other roles).
        if (cfg && cfg.campos_personalizados) {
          const seed = {};
          cfg.campos_personalizados.forEach((f) => {
            if (isAdmin || f.residente_editable === true) {
              seed[f.key] = f.type === 'boolean' ? false : (f.type === 'file' || f.type === 'image' ? [] : '');
            }
          });
          setCampos(seed);
        }
      }
    } catch (error) {
      console.error('Error fetching gestion types:', error);
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' });
      }
    } finally {
      setLoading(false);
    }
  }, [typeKey, isAdmin]);

  React.useEffect(() => {
    if (setNavbarState && navbarState) {
      setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
    }
    fetchTypeConfig();
  }, [typeKey]);

  // Fields rendered in the form: non-admin roles only see
  // residente_editable fields; admins see every field, with admin-only
  // fields rendered after the editable ones (both in array order).
  const editableFields = React.useMemo(() => {
    if (!typeConfig || !typeConfig.campos_personalizados) return [];
    const all = typeConfig.campos_personalizados;
    const residenteEditable = all.filter((f) => f.residente_editable === true);
    if (!isAdmin) return residenteEditable;
    return [...residenteEditable, ...all.filter((f) => f.residente_editable !== true)];
  }, [typeConfig, isAdmin]);

  // Group editable fields by seccion (preserve order).
  const fieldGroups = React.useMemo(() => {
    const groups = [];
    const groupIndex = new Map();
    editableFields.forEach((f) => {
      const sec = f.seccion || null;
      if (!groupIndex.has(sec)) {
        groupIndex.set(sec, groups.length);
        groups.push({ seccion: sec, fields: [] });
      }
      groups[groupIndex.get(sec)].fields.push(f);
    });
    return groups;
  }, [editableFields]);

  const setFieldValue = (key, value, isFile, pickedFiles) => {
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    if (isFile) {
      if (pickedFiles) {
        // New selection: validate then append.
        const fieldDef = editableFields.find((f) => f.key === key);
        const v = validateFiles(pickedFiles, typeConfig, fieldDef ? fieldDef.label : key);
        if (!v.ok) {
          setSnackbar({ open: true, message: v.msg, severity: 'error' });
          return;
        }
        // Optimize each picked file before staging. Optimization never throws;
        // it falls back to the original file on error.
        setOptimizing(true);
        setOptimizeProgress(0);
        (async () => {
          try {
            const optimized = [];
            for (const f of pickedFiles) {
              const onProgress = f.type && f.type.startsWith('video/')
                ? (p) => setOptimizeProgress(Math.max(0, Math.min(100, Math.round(p))))
                : undefined;
              // eslint-disable-next-line no-await-in-loop
              const out = await optimizeFile(f, { onProgress });
              optimized.push(out);
            }
            const existing = Array.isArray(campos[key]) ? campos[key] : [];
            setCampos((prev) => ({ ...prev, [key]: [...existing, ...optimized] }));
          } catch (error) {
            console.error('setFieldValue optimize error:', error);
            const existing = Array.isArray(campos[key]) ? campos[key] : [];
            setCampos((prev) => ({ ...prev, [key]: [...existing, ...pickedFiles] }));
          } finally {
            setOptimizing(false);
            setOptimizeProgress(0);
          }
        })();
      } else {
        // Removal: value is the new array.
        setCampos((prev) => ({ ...prev, [key]: value }));
      }
    } else {
      setCampos((prev) => ({ ...prev, [key]: value }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!titulo.trim()) errs.titulo = 'El título es requerido';
    if (!descripcion.trim()) errs.descripcion = 'La descripción es requerida';
    editableFields.forEach((f) => {
      if (f.required) {
        const v = campos[f.key];
        if (f.type === 'boolean') {
          if (!v) errs[f.key] = 'Este campo es requerido';
        } else if (f.type === 'file' || f.type === 'image') {
          if (!Array.isArray(v) || v.length === 0) errs[f.key] = 'Debes adjuntar al menos un archivo';
        } else {
          if (v === undefined || v === null || String(v).trim() === '') errs[f.key] = 'Este campo es requerido';
        }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setSnackbar({ open: true, message: 'Por favor completa los campos requeridos', severity: 'error' });
      return;
    }
    setShowBackdrop(true);

    // Build campos JSON: file/image fields excluded from the JSON payload
    // (they go as multipart file fields); other fields included by key.
    const camposJson = {};
    editableFields.forEach((f) => {
      if (f.type === 'file' || f.type === 'image') return;
      camposJson[f.key] = campos[f.key];
    });

    const formData = new FormData();
    formData.append('type_key', typeKey);
    formData.append('titulo', titulo);
    formData.append('descripcion', descripcion);
    formData.append('campos', JSON.stringify(camposJson));

    // Append file/image fields. Use the field key as the field name; the
    // backend expects multiple files under "adjuntos".
    editableFields.forEach((f) => {
      if (f.type === 'file' || f.type === 'image') {
        const files = Array.isArray(campos[f.key]) ? campos[f.key] : [];
        files.forEach((file) => formData.append('adjuntos', file));
      }
    });

    // Append general attachments (not tied to a custom field).
    generalAttachments.forEach((file) => formData.append('adjuntos', file));

    try {
      const response = await apiClient.post('gestion/create', formData, {}, { action: 'create_gestion' });
      if (response.status === 200) {
        const newId = response.data && response.data.data && response.data.data.id;
        setSnackbar({ open: true, message: 'Gestión creada correctamente', severity: 'success' });
        setRedirect({ trigger: true, route: newId ? `/gestion/${newId}` : `/gestiones/${typeKey}` });
      }
    } catch (error) {
      const msg = (error && error.data && error.data.data && error.data.data.mensaje)
        || getErrorMessage(error, 'Hubo un error al intentar crear la gestión.');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setShowBackdrop(false);
    }
  };

  const headerTitle = 'Nueva Gestión';

  return (
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && <Navigate to={redirect.route} replace={true} />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: '3.2rem' }}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop open={showBackdrop || loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
        {/* Sticky header */}
        <Box
          sx={{
            position: 'sticky', top: 0, zIndex: 10, px: 3, pt: 2, pb: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <PageHeader title={headerTitle} icon={<GestionTypeIcon typeKey={typeKey} size={26} color="inherit" />} onBack={() => navigate(-1)} />
        </Box>

        <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Completa los datos del problema para poder ser evaluado. Adjunta fotografías o información adicional que facilite su revisión.
          </Typography>

          {/* Standard fields */}
          <Card sx={cardSx}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack spacing={2.5}>
                <Typography sx={sectionTitleSx}>
                  <ExtensionRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                  Información general
                </Typography>
                <TextField
                  fullWidth
                  size="medium"
                  label="Título *"
                  variant="outlined"
                  value={titulo}
                  onChange={(e) => { setTitulo(e.target.value); setErrors((p) => ({ ...p, titulo: undefined })); }}
                  error={!!errors.titulo}
                  helperText={errors.titulo || ''}
                />
                <TextField
                  fullWidth
                  size="medium"
                  label="Descripción *"
                  variant="outlined"
                  rows={4}
                  multiline
                  value={descripcion}
                  onChange={(e) => { setDescripcion(e.target.value); setErrors((p) => ({ ...p, descripcion: undefined })); }}
                  error={!!errors.descripcion}
                  helperText={errors.descripcion || ''}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Custom fields grouped by seccion */}
          {fieldGroups.map((grp, gi) => (
            <Card key={`grp-${gi}`} sx={cardSx}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack spacing={2.5}>
                  {grp.seccion && (
                    <Typography sx={sectionTitleSx}>
                      <ExtensionRoundedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                      {grp.seccion}
                    </Typography>
                  )}
                  {grp.fields.map((f) => (
                    <FieldRenderer
                      key={f.key}
                      field={f}
                      value={campos[f.key]}
                      onChange={(val, isFile, picked) => setFieldValue(f.key, val, isFile, picked)}
                      error={errors[f.key]}
                      typeConfig={typeConfig}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}

          {editableFields.length === 0 && !loading && (
            <Card sx={cardSx}>
              <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
                <Typography color="text.secondary">
                  Este tipo de gestión no tiene campos adicionales.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* General attachments section (always visible, not tied to custom fields) */}
          <Card sx={cardSx}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack spacing={2.5}>
                <Typography sx={sectionTitleSx}>
                  <AttachFileIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                  Adjuntos
                </Typography>
                {generalAttachments.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {generalAttachments.map((f, idx) => (
                      <Chip
                        key={`ga-${idx}`}
                        icon={<AttachFileIcon sx={{ fontSize: '0.9rem' }} />}
                        label={f.name}
                        size="small"
                        onDelete={() => setGeneralAttachments((prev) => prev.filter((_, i) => i !== idx))}
                        sx={{ height: 24, fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                )}
                <Box
                  sx={{
                    border: '2px dashed', borderColor: 'divider', borderRadius: '12px', p: 2,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center',
                  }}
                >
                  <Avatar sx={{ width: 40, height: 40, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'secondary.main' }}>
                    <AttachFileIcon sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                    Arrastra o pulsa para seleccionar
                  </Typography>
                  <label htmlFor="general-adjuntos-input">
                    <Button variant="contained" color="secondary" component="span" startIcon={<AttachFileIcon />} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}>
                      Agregar archivo
                    </Button>
                    <Input
                      type="file"
                      inputProps={{ multiple: true, accept: buildAccept(typeConfig) }}
                      onChange={(e) => {
                        const picked = Array.from(e.target.files || []);
                        const v = validateFiles(picked, typeConfig, 'Adjuntos');
                        if (!v.ok) {
                          setSnackbar({ open: true, message: v.msg, severity: 'error' });
                          return;
                        }
                        setOptimizing(true);
                        setOptimizeProgress(0);
                        (async () => {
                          try {
                            const optimized = [];
                            for (const f of picked) {
                              const onProgress = f.type && f.type.startsWith('video/')
                                ? (p) => setOptimizeProgress(Math.max(0, Math.min(100, Math.round(p))))
                                : undefined;
                              // eslint-disable-next-line no-await-in-loop
                              const out = await optimizeFile(f, { onProgress });
                              optimized.push(out);
                            }
                            setGeneralAttachments((prev) => [...prev, ...optimized]);
                          } catch (error) {
                            console.error('Adjuntos optimize error:', error);
                            setGeneralAttachments((prev) => [...prev, ...picked]);
                          } finally {
                            setOptimizing(false);
                            setOptimizeProgress(0);
                          }
                        })();
                        e.target.value = '';
                      }}
                      id="general-adjuntos-input"
                      sx={{ display: 'none' }}
                    />
                  </label>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>
                    Permitidos: {effectiveAllowedTypes(typeConfig).join(', ') || '—'} · Máx: {effectiveMaxMb(typeConfig)}MB
                  </Typography>
                  {optimizing && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.5 }}>
                        Optimizando... {optimizeProgress > 0 ? `${optimizeProgress}%` : ''}
                      </Typography>
                      <LinearProgress
                        variant={optimizeProgress > 0 ? 'determinate' : 'indeterminate'}
                        value={optimizeProgress}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Footer actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              fullWidth
              color="secondary"
              size="large"
              onClick={handleSubmit}
              disabled={optimizing}
              sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, py: 1.5 }}
            >
              Crear Gestión
            </Button>
            <Button
              variant="text"
              color="primary"
              size="large"
              fullWidth
              sx={{ textTransform: 'none', fontWeight: 700 }}
              onClick={() => setRedirect({ trigger: true, route: `/gestiones/${typeKey}` })}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
